import { apiFetch } from "api/auth";

const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";

interface ApiSuccessResponse<T> {
  success: true;
  message: string;
  data: T;
}

interface ApiErrorResponse {
  success?: false;
  message?: string | string[];
  error?: string;
  statusCode?: number;
}

function getApiErrorMessage(json: ApiErrorResponse | undefined, fallback: string): string {
  if (!json) return fallback;
  if (Array.isArray(json.message)) {
    return json.message.map(String).filter(Boolean).join(" ");
  }
  if (typeof json.message === "string" && json.message.trim()) {
    return json.message;
  }
  if (typeof json.error === "string" && json.error.trim()) {
    return json.error;
  }
  return fallback;
}

// --- Stage 2 / Step 1: Stamp Paper details to Vendor (Follow-Up) ---

export interface FollowUpStampPaperFetched {
  applicant: string | null;
  applicantAddress: string;
  applicantType: string | null;
  signatoryName: string | null;
  signatoryDesignation: string | null;
}

export interface FollowUpStampPaperApplication {
  applicationId: string;
  tmNo: string;
  classNumber: number | null;
  typeOfMark: string | null;
  userDateType: string | null;
  date: string | null;
  numberOfStampPapersForPoa: number;
}

export interface FollowUpStampPaperStepData {
  fetched: FollowUpStampPaperFetched;
  applications: FollowUpStampPaperApplication[];
  totalStampPapersForPoa: number;
  stampVendorName: string | null;
  stampVendorPhone: string | null;
}

export interface FollowUpStampPaperPerApplicationInput {
  applicationId?: string;
  tmNo?: string;
  numberOfStampPapersForPoa?: number;
}

export interface UpdateFollowUpStampPaperDetailsPayload {
  stampPapersPerApplication?: FollowUpStampPaperPerApplicationInput[];
  stampVendorName?: string;
  stampVendorPhone?: string;
}

export async function getFollowUpStampPaperDetails(
  leadId: string
): Promise<FollowUpStampPaperStepData> {
  const res = await apiFetch(`${API_BASE}/follow-up/stamp-paper/${leadId}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  const json = (await res.json()) as
    | ApiSuccessResponse<FollowUpStampPaperStepData>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(
        json as ApiErrorResponse,
        "Failed to load stamp paper details"
      )
    );
  }

  return (json as ApiSuccessResponse<FollowUpStampPaperStepData>).data;
}

export async function updateFollowUpStampPaperDetails(
  leadId: string,
  payload: UpdateFollowUpStampPaperDetailsPayload
): Promise<FollowUpStampPaperStepData> {
  const res = await apiFetch(`${API_BASE}/follow-up/stamp-paper/${leadId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const json = (await res.json()) as
    | ApiSuccessResponse<FollowUpStampPaperStepData>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(
        json as ApiErrorResponse,
        "Failed to update stamp paper details"
      )
    );
  }

  return (json as ApiSuccessResponse<FollowUpStampPaperStepData>).data;
}

export async function getFollowUpStampPaperPreview(
  leadId: string
): Promise<{ message: string; canSend: boolean }> {
  const res = await apiFetch(
    `${API_BASE}/follow-up/stamp-paper/${leadId}/preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ message: string; canSend: boolean }>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(
        json as ApiErrorResponse,
        "Failed to load stamp paper preview"
      )
    );
  }

  return (json as ApiSuccessResponse<{ message: string; canSend: boolean }>).data;
}

export async function sendFollowUpStampPaperWhatsApp(
  leadId: string
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/follow-up/stamp-paper/${leadId}/send-whatsapp`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(
        json as ApiErrorResponse,
        "Failed to send stamp paper details to vendor"
      )
    );
  }

  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

// --- Stage 2 / Step 2: Stamp Paper from Vendor (Follow-Up) ---

export interface FollowUpStampPaperFromVendorApplication {
  applicationId: string;
  tmNo: string;
  numberOfStampPapersForPoa: number;
  amountPayableToStampVendor: number;
  paidToVendor: boolean | null;
}

export interface FollowUpStampPaperFromVendorData {
  applications: FollowUpStampPaperFromVendorApplication[];
  totalStampPapersForPoa: number;
  totalAmountPayableToStampVendor: number;
  paymentStatusDisplay: string;
}

export interface FollowUpStampPaperFromVendorPaymentInput {
  applicationId?: string;
  tmNo?: string;
  amountPayableToStampVendor?: number;
  paidToVendor?: boolean;
}

export interface UpdateFollowUpStampPaperFromVendorPayload {
  paymentPerApplication?: FollowUpStampPaperFromVendorPaymentInput[];
}

export async function getFollowUpStampPaperFromVendor(
  leadId: string
): Promise<FollowUpStampPaperFromVendorData> {
  const res = await apiFetch(
    `${API_BASE}/follow-up/stamp-paper/${leadId}/from-vendor`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<FollowUpStampPaperFromVendorData>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(
        json as ApiErrorResponse,
        "Failed to load stamp paper from vendor details"
      )
    );
  }

  return (json as ApiSuccessResponse<FollowUpStampPaperFromVendorData>).data;
}

export async function updateFollowUpStampPaperFromVendor(
  leadId: string,
  payload: UpdateFollowUpStampPaperFromVendorPayload
): Promise<FollowUpStampPaperFromVendorData> {
  const res = await apiFetch(
    `${API_BASE}/follow-up/stamp-paper/${leadId}/from-vendor`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<FollowUpStampPaperFromVendorData>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(
        json as ApiErrorResponse,
        "Failed to update stamp paper from vendor details"
      )
    );
  }

  return (json as ApiSuccessResponse<FollowUpStampPaperFromVendorData>).data;
}

// --- Stage 2 / Step 3: Preparation of Power of Attorney (Follow-Up) ---

export interface FollowUpPowerOfAttorneyPdfMeta {
  key: string;
  url: string;
  filename: string;
}

export interface FollowUpPowerOfAttorneyItem {
  poaNo: number;
  classNumber: number;
  typeOfMark: string;
  isRequired: boolean;
  status: string | null;
  mark: string | null;
  applicant: string | null;
  applicantAddress: string | null;
  applicantType: string | null;
  signatoryName: string | null;
  signatoryDesignation: string | null;
  dateOfPowerOfAttorney: string | null;
  dateOfPowerOfAttorneyFormatted: string | null;
  descriptionOfApplicant: string | null;
  templateVariant: string;
  templateFiles: string[];
  pdf: FollowUpPowerOfAttorneyPdfMeta | null;
}

export interface FollowUpPowerOfAttorneyContextResponse {
  powerOfAttorney: FollowUpPowerOfAttorneyItem;
}

export interface UpdateFollowUpPowerOfAttorneyPayload {
  mark?: string;
  isRequired?: boolean;
  dateOfPowerOfAttorney?: string;
  descriptionOfApplicant?: string;
  status?: string;
}

export async function getFollowUpPowerOfAttorneyContext(
  leadId: string
): Promise<FollowUpPowerOfAttorneyContextResponse> {
  const res = await apiFetch(
    `${API_BASE}/follow-up/power-of-attorney/${leadId}`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<FollowUpPowerOfAttorneyContextResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(
        json as ApiErrorResponse,
        "Failed to load Power of Attorney context"
      )
    );
  }

  return (json as ApiSuccessResponse<FollowUpPowerOfAttorneyContextResponse>).data;
}

export async function updateFollowUpPowerOfAttorneyEntry(
  leadId: string,
  payload: UpdateFollowUpPowerOfAttorneyPayload
): Promise<FollowUpPowerOfAttorneyContextResponse> {
  const res = await apiFetch(
    `${API_BASE}/follow-up/power-of-attorney/${leadId}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<FollowUpPowerOfAttorneyContextResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(
        json as ApiErrorResponse,
        "Failed to update Power of Attorney entry"
      )
    );
  }

  return (json as ApiSuccessResponse<FollowUpPowerOfAttorneyContextResponse>).data;
}

export async function generateFollowUpPowerOfAttorneyPdf(
  leadId: string
): Promise<FollowUpPowerOfAttorneyPdfMeta> {
  const res = await apiFetch(
    `${API_BASE}/follow-up/power-of-attorney/${leadId}/generate-pdf`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<FollowUpPowerOfAttorneyPdfMeta>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(
        json as ApiErrorResponse,
        "Failed to generate Power of Attorney PDF"
      )
    );
  }

  return (json as ApiSuccessResponse<FollowUpPowerOfAttorneyPdfMeta>).data;
}

// --- Stage 2 / Step 4: File TM-M for Follow-up ---

export interface FollowUpTmMDocumentMeta {
  key: string;
  url: string;
  filename: string;
}

export interface FollowUpTmMApplication {
  applicationId: string;
  /** 3c) Application Number (TM No.) */
  tmNo: string;
  mark: string | null;
  /** 11p) Date of Filing TM-M for Follow-up. Backend returns DD-MM-YYYY. */
  dateOfFilingTmM: string | null;
  tmMDocument: FollowUpTmMDocumentMeta | null;
  status: string | null;
}

export interface FollowUpTmMContextResponse {
  applications: FollowUpTmMApplication[];
}

export interface UpdateFollowUpTmMEntryPayload {
  tmNo?: string;
  /** ISO date YYYY-MM-DD */
  dateOfFilingTmM?: string;
  /** S3 key – must be sent together with filename */
  key?: string;
  filename?: string;
}

export interface BulkUpdateFollowUpTmMEntryPayload extends UpdateFollowUpTmMEntryPayload {
  tmNo: string;
}

export async function getFollowUpTmMContext(
  leadId: string
): Promise<FollowUpTmMContextResponse> {
  const res = await apiFetch(`${API_BASE}/follow-up/tm-m/${leadId}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  const json = (await res.json()) as
    | ApiSuccessResponse<FollowUpTmMContextResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(json as ApiErrorResponse, "Failed to load File TM-M context")
    );
  }

  return (json as ApiSuccessResponse<FollowUpTmMContextResponse>).data;
}

export async function updateFollowUpTmMEntry(
  leadId: string,
  applicationId: string,
  payload: UpdateFollowUpTmMEntryPayload
): Promise<FollowUpTmMContextResponse> {
  const res = await apiFetch(
    `${API_BASE}/follow-up/tm-m/${leadId}/application/${applicationId}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<FollowUpTmMContextResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(
        json as ApiErrorResponse,
        "Failed to update File TM-M entry"
      )
    );
  }

  return (json as ApiSuccessResponse<FollowUpTmMContextResponse>).data;
}

export async function bulkUpdateFollowUpTmMEntries(
  leadId: string,
  items: BulkUpdateFollowUpTmMEntryPayload[]
): Promise<FollowUpTmMContextResponse> {
  const res = await apiFetch(`${API_BASE}/follow-up/tm-m/${leadId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items }),
  });

  const json = (await res.json()) as
    | ApiSuccessResponse<FollowUpTmMContextResponse>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(
        json as ApiErrorResponse,
        "Failed to update File TM-M entries"
      )
    );
  }

  return (json as ApiSuccessResponse<FollowUpTmMContextResponse>).data;
}

export async function saveFollowUpTmMDocument(
  leadId: string,
  applicationId: string,
  payload: { key: string; filename: string }
): Promise<FollowUpTmMDocumentMeta & { applicationId: string; tmNo: string }> {
  const res = await apiFetch(
    `${API_BASE}/follow-up/tm-m/${leadId}/application/${applicationId}/save-document`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<
        FollowUpTmMDocumentMeta & { applicationId: string; tmNo: string }
      >
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(
        json as ApiErrorResponse,
        "Failed to save File TM-M document"
      )
    );
  }

  return (
    json as ApiSuccessResponse<
      FollowUpTmMDocumentMeta & { applicationId: string; tmNo: string }
    >
  ).data;
}

// --- Stage 2 / Step 5: Inform Client about Filing of TM-M for Follow-up ---

export async function getFollowUpInformClientTmMFiledPreview(
  leadId: string
): Promise<{ message: string; applicationCount: number }> {
  const res = await apiFetch(
    `${API_BASE}/follow-up/tm-m/${leadId}/inform-client-preview`,
    { method: "GET", headers: { "Content-Type": "application/json" } }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ message: string; applicationCount: number }>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(
        json as ApiErrorResponse,
        "Failed to load Inform Client (TM-M filed) preview"
      )
    );
  }

  return (
    json as ApiSuccessResponse<{ message: string; applicationCount: number }>
  ).data;
}

export async function sendFollowUpInformClientTmMFiledWhatsApp(
  leadId: string
): Promise<{ waLink: string }> {
  const res = await apiFetch(
    `${API_BASE}/follow-up/tm-m/${leadId}/inform-client/send-whatsapp`,
    { method: "POST", headers: { "Content-Type": "application/json" } }
  );

  const json = (await res.json()) as
    | ApiSuccessResponse<{ waLink: string }>
    | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(
      getApiErrorMessage(
        json as ApiErrorResponse,
        "Failed to send Inform Client (TM-M filed) to client WhatsApp"
      )
    );
  }

  return (json as ApiSuccessResponse<{ waLink: string }>).data;
}

/** Backend FollowUpStep enum values for Stage 2 (Filing) progress lookup. */
export const FollowUpFilingStep = {
  FU_S2_01_StampPaperDetailsToVendor: "FU_S2_01_STAMP_PAPER_DETAILS_TO_VENDOR",
  FU_S2_02_PoaPreparation: "FU_S2_02_POA_PREPARATION",
  FU_S2_03_FileTmM: "FU_S2_03_FILE_TM_M",
  FU_S2_04_InformClientTmMFiled: "FU_S2_04_INFORM_CLIENT_TM_M_FILED",
} as const;
